import { readableOn, errorWords } from "@alm/domain";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { SearchField } from "@/components/app/patterns/search-field";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { RowActions } from "@/components/app/patterns/row-actions";
import { SharedRowNote } from "@/components/app/lists/shared-row-note";
import { RecordCard } from "@/components/app/lists/record-card";
import { EmptyRows } from "@/components/app/lists/empty-rows";
import { RecordRow } from "@/components/app/lists/record-row";
import { RemoveConfirm } from "@/components/app/fields/remove-confirm";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { useFacility } from "@/lib/facility/facility";
import { searchPatch, optionalSearch } from "@/lib/search";
import { orpc } from "@/lib/orpc";
import { useNewRowPlace } from "@/hooks/use-new-row-place";
import { useCan, usePolicy } from "@/lib/policy";
import { reachLabel } from "./-components/messaging/facility-reach-field";
import { NoMatches } from "@/routes/_authenticated/_administration/settings/-components/no-matches";
import { RowsSkeleton } from "@/routes/_authenticated/_administration/settings/-components/rows-skeleton";
import { TagFormDialog, type TagDraft } from "./-components/billing/tag-form-dialog";
import { TagUsageDialog } from "./-components/billing/tag-usage-dialog";
import { guardRoute } from "@/lib/policy/route-guard";

export const Route = createFileRoute("/_authenticated/_administration/settings/tags")({
  validateSearch: (search: Record<string, unknown>): { q?: string } => ({
    q: optionalSearch(search.q),
  }),
  beforeLoad: ({ context }) => guardRoute(context.me, "tags"),
  component: TagsSettings,
});

const fail = (error: Error) => toast.error(errorWords(error));

type ReachOf = { appliesToAllFacilities: boolean; facilityIds: readonly string[] };

/** The labels a building puts on people, and where each one reaches. */
function TagsSettings() {
  const { facilityId } = useFacility();
  /* As the API curates the list: a tag with a home building is kept with settings:manage there,
     and one with none (every building) with it for the company plus Administrator actions
     (`assertCurator`). Saving a tag's reach asks settings:manage at every building it names. */
  const { can } = usePolicy();
  const keepsCompanySettings = useCan("settings:manage", null);
  const administers = useCan("organization:administer", null);
  const curatesShared = keepsCompanySettings && administers;
  const place = useNewRowPlace("settings:manage");
  const canManage = curatesShared || place.buildings.length > 0;
  const keepsHome = (tag: { facilityId: string | null }) =>
    tag.facilityId ? can("settings:manage", tag.facilityId) : curatesShared;
  const editable = (tag: { facilityId: string | null } & ReachOf) =>
    keepsHome(tag) &&
    (tag.appliesToAllFacilities
      ? curatesShared
      : tag.facilityIds.every((id) => can("settings:manage", id)));
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<TagDraft | null>(null);
  const [open, setOpen] = useState(false);
  const [removing, setRemoving] = useState<{ id: string; name: string } | null>(null);
  const [inspecting, setInspecting] = useState<{ id: string; name: string } | null>(null);
  const { q } = Route.useSearch();
  const patch = searchPatch(useNavigate({ from: Route.fullPath }));
  const { text: search, change: setSearch } = useSearchParamText(q, (next) => patch({ q: next }));

  const tags = useQuery(
    orpc.administration.catalogs.tags.list.queryOptions({ input: { facilityId } }),
  );
  const facilities = useQuery(
    orpc.administration.facilities.list.queryOptions({ input: { includeInactive: false } }),
  );
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: orpc.administration.catalogs.tags.key() });

  const remove = useMutation(
    orpc.administration.catalogs.tags.remove.mutationOptions({
      onSuccess: async () => {
        toast.success("Tag deleted");
        setRemoving(null);
        await invalidate();
      },
      onError: fail,
    }),
  );

  const edit = (tag: TagDraft | null) => {
    setEditing(tag);
    setOpen(true);
  };

  const needle = search.trim().toLowerCase();
  const rows = (tags.data ?? []).filter(
    (tag) =>
      !needle ||
      tag.name.toLowerCase().includes(needle) ||
      (tag.description ?? "").toLowerCase().includes(needle),
  );

  return (
    <>
      <PageHeader
        title="Tags"
        meta="Labels you put on residents and staff, and the buildings each one is offered in."
      />

      <SearchField value={search} onChange={setSearch} placeholder="Search tags" />

      <RecordCard
        title="All tags"
        count={tags.data ? rows.length : undefined}
        canWrite={canManage}
        addLabel="Add tag"
        onAdd={() => edit(null)}
      >
        {tags.isPending ? (
          <RowsSkeleton />
        ) : tags.isError ? (
          <ReadFailed
            message="The tags could not be loaded."
            error={tags.error}
            retrying={tags.isFetching}
            onRetry={() => void tags.refetch()}
          />
        ) : rows.length === 0 ? (
          needle ? (
            <NoMatches search={search} onClear={() => setSearch("")} />
          ) : (
            <EmptyRows text="No tags yet. Add one to mark residents or staff, like Fall risk or Hospice." />
          )
        ) : (
          <ul className="divide-y">
            {rows.map((tag) => (
              <RecordRow
                key={tag.id}
                title={
                  <span className="flex items-center gap-2">
                    <span
                      className="inline-flex h-5 items-center rounded-xs px-1.5 text-xs font-medium"
                      style={{ backgroundColor: tag.color, color: readableOn(tag.color) }}
                    >
                      {tag.name}
                    </span>
                  </span>
                }
                meta={[
                  reachLabel(tag.appliesToAllFacilities, tag.facilityIds, facilities.data ?? []),
                  tag.description,
                ]
                  .filter(Boolean)
                  .join(", ")}
                actions={
                  keepsHome(tag) ? (
                    <RowActions
                      modal={false}
                      label={tag.name}
                      primary={
                        editable(tag) && {
                          label: "Edit",
                          onSelect: () =>
                            edit({
                              id: tag.id,
                              name: tag.name,
                              color: tag.color,
                              description: tag.description,
                              appliesToAllFacilities: tag.appliesToAllFacilities,
                              facilityIds: tag.facilityIds,
                            }),
                        }
                      }
                      items={[
                        {
                          label: "See where it is used",
                          onSelect: () => setInspecting({ id: tag.id, name: tag.name }),
                        },
                        {
                          label: "Delete tag",
                          destructive: true,
                          onSelect: () => setRemoving({ id: tag.id, name: tag.name }),
                        },
                      ]}
                    />
                  ) : (
                    <>
                      {canManage && !tag.facilityId ? <SharedRowNote /> : null}
                      <RowActions
                        label={tag.name}
                        modal={false}
                        primary={{
                          label: "See where it is used",
                          onSelect: () => setInspecting({ id: tag.id, name: tag.name }),
                        }}
                        items={[]}
                      />
                    </>
                  )
                }
              />
            ))}
          </ul>
        )}
      </RecordCard>

      {inspecting ? <TagUsageDialog tag={inspecting} onClose={() => setInspecting(null)} /> : null}

      {removing ? (
        <RemoveConfirm
          title={`Delete “${removing.name}”?`}
          confirmLabel="Delete tag"
          description="Everybody carrying this tag loses it, and the history of who was tagged with it goes too. Nothing about the people themselves changes."
          onConfirm={() => remove.mutate({ tagId: removing.id })}
          onClose={() => setRemoving(null)}
        />
      ) : null}

      {open ? (
        <TagFormDialog
          key={editing?.id ?? "new"}
          editing={editing}
          open={open}
          facilities={place.buildings}
          everywhereAllowed={curatesShared}
          startAt={place.startAt}
          onOpenChange={setOpen}
          onSaved={invalidate}
        />
      ) : null}
    </>
  );
}
